import { FormSectionLayout, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { OptionsFilterBar } from "src/routes/projects/change-events/guided/workflows/components/OptionsFilterBar";
import {
  createOptionGroupSection,
  SelectOptionsFormInput,
  selectOptionsRowsFormConfig,
  selectOptionsRowsMapToForm,
  showGroup,
  sortGroupOptions,
  toSelectOptionsSections,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils";
import {
  SelectOptionsUtils_CheckoutConfigFragment,
  SelectOptionsUtils_OptionGroupFragment,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, idsOf, queryResult } from "src/utils";
import { useFormState } from "src/utils/formState";
import { useSelectOptionsStepQuery } from "./1.SelectOptionsStep.generated";
import { AddOrModifyOptionsFormInput } from "./addOrModifyOptionsWorkflow";
import { useSaveOptionSelection } from "./useSaveOptionSelection";

type SelectOptionsStepProps = { formState: ObjectState<AddOrModifyOptionsFormInput> };

/**
 * Step 1 of Add/Modify Options: the project's current configuration as the checkout's sections, each option group on
 * the card its shape calls for. Every pick is auto-saved on the change event, and the reconciled selection the server
 * answers with is what `formState.configChange.optionIds` holds.
 */
export function SelectOptionsStep(props: SelectOptionsStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const configId = useComputed(() => formState.configChange.resultId.value, [formState]);
  const query = useSelectOptionsStepQuery({
    variables: { configId: configId ?? fail("Change event has no result config"), structureId: projectId },
    fetchPolicy: "cache-and-network",
  });
  return queryResult(query, ({ checkoutConfig }) => (
    <SelectOptionsStepView checkoutConfig={checkoutConfig} formState={formState} />
  ));
}

type SelectOptionsStepViewProps = {
  checkoutConfig: SelectOptionsUtils_CheckoutConfigFragment;
  formState: ObjectState<AddOrModifyOptionsFormInput>;
};

function SelectOptionsStepView(props: SelectOptionsStepViewProps) {
  const { checkoutConfig, formState } = props;
  const [showExpired, setShowExpired] = useState(false);
  const priceMode = useComputed(() => formState.priceMode.value ?? OptionPriceMode.Price, [formState]);
  // The options' order is fixed on first load, like checkout, so a pick doesn't jump its card to the top of the group
  const [firstSort] = useState(() => sortGroupOptions(checkoutConfig.config.optionGroups));
  const groups = useMemo(
    () => sortGroupOptions(checkoutConfig.config.optionGroups, firstSort),
    [checkoutConfig, firstSort],
  );
  // The cards are locked while a save is in flight, so every pick is made against the server's latest answer
  const { save, saving } = useSaveOptionSelection(formState, groups);
  const optionIds = useComputed(() => formState.configChange.optionIds.value, [formState]);
  const rowsInput = useMemo(() => ({ groups, optionIds }), [groups, optionIds]);

  const rowsForm = useFormState({
    config: selectOptionsRowsFormConfig,
    init: { input: rowsInput, map: selectOptionsRowsMapToForm },
    readOnly: saving,
    // Auto-save every pick because only the backend knows how a change cascades: it prunes options whose
    // prerequisites are no longer met or that now conflict, adds required options and personalization defaults (e.g.
    // turning "Add Washer" on picks a default washer), and re-prices the result. Each card's `canBeSelected` /
    // `isSelected` / prices are computed against that saved selection, so the user is always picking against the
    // server's latest answer rather than a stale one.
    autoSave: async () => {
      const toggled = rowsForm.rows.rows
        .filter((row) => row.dirty)
        .flatMap((row) => [row.optionId.originalValue, row.optionId.value])
        .compact();
      const result = await save(toOptionIds(formState, rowsForm.rows.value, groups), toggled);
      // A dirty row keeps its value through a refresh, which would hide a pick the server reconciled away
      rowsForm.revertChanges();
      if (result) {
        rowsForm.update({
          groups: result.config.optionGroups,
          optionIds: idsOf(result.config.options, "readyPlanOption"),
        });
      }
    },
  });

  const rowByRpogId = rowsForm.rows.rows.keyBy((row) => row.groupId.value);
  const rowFor = (rpogId: string) => rowByRpogId[rpogId] ?? fail(`No row for group ${rpogId}`);

  const sections = toSelectOptionsSections(groups, checkoutConfig.interiorSubSteps).map((section) => ({
    title: section.title,
    childSections: section.groups
      .filter((group) => showGroup(group, rowFor(group.group.id).value, showExpired))
      .map((group) => createOptionGroupSection(group, groups, rowFor, priceMode)),
  }));

  return (
    <FormSectionLayout
      title="Select Options"
      description="This shows the current options available for this project's product offering. Add, remove, or change options to build the change event."
      withJumpLinks
      withAutoSave
      initialFields={
        <OptionsFilterBar
          showExpired={showExpired}
          onShowExpired={setShowExpired}
          priceMode={priceMode}
          onPriceMode={(mode) => formState.priceMode.set(mode)}
        />
      }
      sections={sections.filter((section) => section.childSections.nonEmpty)}
    />
  );
}

/**
 * The selection the rows save: the change event's whole `optionIds`, replacing the side's selection like checkout does.
 * A row counts when it's `selected` and has an `optionId`, and a child group's row (the "Add Washer and Dryer"
 * variants) only while its parent's row is `selected` too. Ids already selected that belong to no loaded group
 * (`showInCheckout=false` options) are kept.
 */
function toOptionIds(
  formState: ObjectState<AddOrModifyOptionsFormInput>,
  rows: SelectOptionsFormInput[],
  groups: SelectOptionsUtils_OptionGroupFragment[],
): string[] {
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const rowByGroupId = rows.keyBy((row) => row.groupId);
  const loadedOptionIds = new Set(groups.flatMap((ccfgog) => ccfgog.options.map((ccfgpo) => ccfgpo.option.id)));
  const picked = rows
    .filter((row) => {
      if (!row.selected || !row.optionId) return false;
      const parentId = groupByRpogId[row.groupId]?.parentGroup?.id;
      return !parentId || !!rowByGroupId[parentId]?.selected;
    })
    .map((row) => row.optionId!);
  const kept = (formState.configChange.optionIds.value ?? []).filter((id) => !loadedOptionIds.has(id));
  return [...kept, ...picked].unique();
}
